import cx from 'classnames';
import { CSSProperties, ReactNode, useCallback, useRef } from 'react';
import ChevronLeftIcon from '../../icons/ChevronLeftIcon';
import ChevronRightIcon from '../../icons/ChevronRightIcon';
import useScrollToNextElement from '../../utils/useScrollToNextElement';
import TabItem from './TabItem';
import styles from './TabList.module.css';
import { useScrollCurrentTabIntoView } from './useScrollCurrentTabIntoView';
import { useTabEdgeVisibility } from './useTabEdgeVisibility';

export type Tab<T = string> = {
  'id': T;
  'label': ReactNode | string | number;
  'isTabHighlighted'?: boolean;
  'isTabDisabled'?: boolean;
  'onTabClick'?: (tab?: Tab<T>) => void;
  'styles'?: CSSProperties;
  'data-testid'?: string;
};

export type TabListTheme = 'light' | 'dark';

export type TablistProps<T = string> = {
  'tabs': Tab<T>[];
  'currentTab': Tab<T> | null;
  'setCurrentTab': (tab: Tab<T>) => void;
  'rightSideComponent'?: ReactNode;
  'className'?: string;
  'data-testid'?: string;
  'isInactiveTabBlue'?: boolean;
  'theme'?: TabListTheme;
  'hideBottomBorder'?: boolean;
  /** Renders the tab bar with `position: sticky` and horizontal page bleed. */
  'sticky'?: boolean;
  /** CSS `top` value for sticky positioning (e.g. `'0'`, `'4rem'`). Defaults to the CES header height. Only effective when `sticky` is true. */
  'stickyTop'?: string;
};

type TabListNavStyle = CSSProperties & {
  '--tab-list-left-gradient-offset'?: string;
  '--tab-list-sticky-top'?: string;
};

export default function TabList<T>({
  tabs,
  currentTab,
  setCurrentTab,
  rightSideComponent,
  className,
  isInactiveTabBlue,
  theme = 'light',
  hideBottomBorder = false,
  sticky = false,
  stickyTop,
  'data-testid': dataTestId = 'tabList',
}: TablistProps<T>) {
  const tabsContainerRef = useRef<HTMLUListElement>(null);
  const firstTabRef = useRef<HTMLLIElement>(null);
  const lastTabRef = useRef<HTMLLIElement>(null);

  const scrollToNextTab = useScrollToNextElement(tabsContainerRef);

  const { isFirstTabVisible, isLastTabVisible, leftGradientOffset } = useTabEdgeVisibility({
    tabs,
    tabsContainerRef,
    firstTabRef,
    lastTabRef,
  });

  useScrollCurrentTabIntoView({ currentTab, tabsContainerRef });

  const getTabRef = useCallback(
    (index: number) => {
      return index === 0 ? firstTabRef : index === tabs.length - 1 ? lastTabRef : null;
    },
    [tabs],
  );

  const handleOnTabClick = useCallback(
    (tab: Tab<T>) => {
      if (tab.isTabDisabled) return;
      setCurrentTab(tab);
      tab.onTabClick?.(tab);
    },
    [setCurrentTab],
  );

  const navStyle: TabListNavStyle = {
    '--tab-list-left-gradient-offset': `${leftGradientOffset}px`,
    ...(sticky && stickyTop !== undefined ? { '--tab-list-sticky-top': stickyTop } : {}),
  };

  return (
    <nav
      data-testid={dataTestId}
      data-theme={theme}
      data-sticky={sticky ? 'true' : undefined}
      className={cx(styles.tabsContainer, className, {
        [styles.hideBottomBorder]: hideBottomBorder,
        [styles.leftGradient]: !isFirstTabVisible,
      })}
      style={navStyle}
    >
      <ul ref={tabsContainerRef} role="tablist" className={styles.tabsList}>
        {tabs.map((tab, index) => (
          <TabItem
            key={String(tab.id) + tab.label}
            tab={tab}
            isActive={currentTab ? tab.id === currentTab.id : index === 0}
            isBlueInactive={Boolean(currentTab && isInactiveTabBlue && tab.id !== currentTab.id)}
            tabRef={getTabRef(index)}
            onTabClick={handleOnTabClick}
          />
        ))}
      </ul>
      {(!isFirstTabVisible || !isLastTabVisible) && (
        <section
          className={cx(styles.slidersContainer, {
            [styles.rightGradient]: !isLastTabVisible,
          })}
        >
          <button
            aria-label="slide to the left"
            className={styles.slider}
            onClick={() => scrollToNextTab('left')}
            disabled={isFirstTabVisible}
          >
            <ChevronLeftIcon />
          </button>
          <button
            aria-label="slide to the right"
            className={styles.slider}
            onClick={() => scrollToNextTab('right')}
            disabled={isLastTabVisible}
          >
            <ChevronRightIcon />
          </button>
        </section>
      )}
      {rightSideComponent && <section className={styles.rightSideComponent}>{rightSideComponent}</section>}
    </nav>
  );
}
